import React, { useEffect } from 'react'
import type { DemoTheme } from '../types'

interface ThemeEditorProps {
  theme?: DemoTheme
  onSetTheme: (theme: DemoTheme) => void
}

const ACCENT_PRESETS = [
  { name: 'Indigo', value: '#5048e5' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Slate', value: '#0f172a' },
]

export const FONT_PRESETS = [
  { name: 'System', value: '', family: 'ui-sans-serif, system-ui, sans-serif' },
  { name: 'Inter', value: 'Inter', family: '"Inter", sans-serif' },
  { name: 'Space Grotesk', value: 'Space Grotesk', family: '"Space Grotesk", sans-serif' },
  { name: 'DM Sans', value: 'DM Sans', family: '"DM Sans", sans-serif' },
  { name: 'Playfair', value: 'Playfair Display', family: '"Playfair Display", serif' },
  { name: 'Merriweather', value: 'Merriweather', family: '"Merriweather", serif' },
  { name: 'JetBrains Mono', value: 'JetBrains Mono', family: '"JetBrains Mono", monospace' },
  { name: 'Outfit', value: 'Outfit', family: '"Outfit", sans-serif' },
]

/** Load a Google Font dynamically */
export function loadGoogleFont(fontName: string) {
  if (!fontName) return
  const id = `gfont-${fontName.replace(/\s+/g, '-')}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`
  document.head.appendChild(link)
}

/** Get the CSS font-family string for a theme font value */
export function getFontFamily(fontValue?: string): string | undefined {
  if (!fontValue) return undefined
  const preset = FONT_PRESETS.find((f) => f.value === fontValue)
  return preset?.family || `"${fontValue}", sans-serif`
}

function PropertyLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
      {children}
    </label>
  )
}

export default function ThemeEditor({ theme, onSetTheme }: ThemeEditorProps) {
  const currentAccent = theme?.accent || '#5048e5'
  const currentFont = theme?.font || ''

  // Load all font previews
  useEffect(() => {
    FONT_PRESETS.forEach((f) => loadGoogleFont(f.value))
  }, [])

  const updateTheme = (updates: Partial<DemoTheme>) => {
    onSetTheme({ accent: currentAccent, font: currentFont || undefined, ...updates })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Accent Color</PropertyLabel>
        <div className="flex flex-wrap gap-2">
          {ACCENT_PRESETS.map((preset) => (
            <button
              key={preset.name}
              title={preset.name}
              onClick={() => updateTheme({ accent: preset.value })}
              className={`size-7 rounded-full transition-all ${
                currentAccent === preset.value
                  ? 'ring-2 ring-offset-2 ring-current scale-110'
                  : 'hover:scale-110'
              }`}
              style={{ backgroundColor: preset.value, color: preset.value }}
            />
          ))}
        </div>
      </div>

      {/* Custom color input */}
      <div className="space-y-2">
        <PropertyLabel>Custom</PropertyLabel>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={currentAccent}
            onChange={(e) => updateTheme({ accent: e.target.value })}
            className="size-8 rounded cursor-pointer border border-slate-200"
          />
          <input
            type="text"
            value={currentAccent}
            onChange={(e) => {
              if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
                updateTheme({ accent: e.target.value })
              }
            }}
            className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm font-mono focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
            placeholder="#5048e5"
          />
        </div>
      </div>

      {/* Font picker */}
      <div className="space-y-2 pt-4 border-t border-slate-100">
        <PropertyLabel>Font</PropertyLabel>
        <div className="space-y-1">
          {FONT_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => updateTheme({ font: preset.value || undefined })}
              className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                currentFont === preset.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-slate-900'
                  : 'border-transparent text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span
                className="text-sm block"
                style={{ fontFamily: preset.family }}
              >
                {preset.name}
              </span>
              <span
                className="text-xs text-slate-400 block mt-0.5"
                style={{ fontFamily: preset.family }}
              >
                The quick brown fox jumps
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
